import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Typography } from '../../../../components/common/Typography/Typography';
import { env } from '../../../../config/env';
import { Developer, GameId } from '../../../../constants/enums';
import { EXTERNAL_LINKS } from '../../../../constants/links';
import { getGameProfileRoute } from '../../../../constants/routes';
import { useDocumentHead } from '../../../../hooks/useDocumentHead/useDocumentHead';
import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { toCssUrl } from '../../../../services/urlSafety';
import { useGetGamesQuery } from '../../../games/api/gamesApi';
import { Game } from '../../../games/types/gameTypes';
import { useGetModsQuery } from '../../../mods/api/modsApi';
import { ModCard } from '../../../mods/components/ModCard/ModCard';
import { Mod } from '../../../mods/types/modTypes';
import { getThumbnailSrc } from '../../../mods/utils/mediaUtils';
import styles from './SimrLabsScreen.module.css';
import { SimrLabsScreenProps } from './SimrLabsScreen.types';

export const SimrLabsScreen: React.FC<SimrLabsScreenProps> = ({ className = '' }) => {
  const { t } = useTranslation();
  const heroBgUrl = `${env().PUBLIC_URL}/images/home/home-hero-bg.jpg`;

  const [displayedBg, setDisplayedBg] = useState<string>(heroBgUrl);
  const [incomingBg, setIncomingBg] = useState<string | null>(null);

  useDocumentHead({
    title: `${t.seo.simrlabsTitle} - ${t.brand}`,
    description: t.seo.simrlabsDescription,
  });

  const { data: games, isLoading: isGamesLoading, error: gamesError } = useGetGamesQuery();

  const { data: mods, isLoading: isModsLoading, error: modsError } = useGetModsQuery();

  const inHouseGames = useMemo(() => {
    if (!games) return [];
    return games.filter((game) => game.developer === Developer.SIMRLABS);
  }, [games]);

  const inHouseMods = useMemo(() => {
    if (!mods) return [];
    return mods.filter((mod) => mod.modAuthor === Developer.SIMRLABS);
  }, [mods]);

  const currentBg = displayedBg || heroBgUrl;

  const handleAnimationEnd = useCallback((bg: string) => {
    setDisplayedBg(bg);
    setIncomingBg(null);
  }, []);

  useEffect(() => {
    if (!incomingBg) return;
    const timer = setTimeout(() => {
      setDisplayedBg(incomingBg);
      setIncomingBg(null);
    }, 500);
    return () => clearTimeout(timer);
  }, [incomingBg]);

  const handleGameHover = useCallback(
    (game: Game) => {
      const bgUrl =
        game.id === GameId.SAIL_TO_UNKNOWN
          ? EXTERNAL_LINKS.SAIL_TO_UNKNOWN_CONTAINER_BG
          : game.heroBannerUrl || game.thumbnailUrl;

      if (!bgUrl || !bgUrl.trim()) return;
      if (bgUrl === currentBg || bgUrl === incomingBg) return;

      setIncomingBg(bgUrl);
    },
    [currentBg, incomingBg]
  );

  const handleModHover = useCallback(
    (mod: Mod) => {
      if (!mod.thumbnailUrl || !mod.thumbnailUrl.trim()) {
        return;
      }
      const thumbUrl = getThumbnailSrc(mod.thumbnailUrl);
      if (!thumbUrl) return;

      if (thumbUrl === currentBg || thumbUrl === incomingBg) {
        return;
      }

      setIncomingBg(thumbUrl);
    },
    [currentBg, incomingBg]
  );

  return (
    <div className={`${styles.simrlabsWrapper} ${className}`.trim()}>
      {currentBg && (
        <div
          className={styles.pageBlurredBackdrop}
          style={{ backgroundImage: toCssUrl(currentBg) }}
          aria-hidden="true"
          data-testid="simrlabs-blurred-backdrop"
        />
      )}
      {incomingBg && (
        <div
          key={incomingBg}
          className={`${styles.pageBlurredBackdrop} ${styles.fadeIn}`}
          style={{ backgroundImage: toCssUrl(incomingBg) }}
          onAnimationEnd={() => handleAnimationEnd(incomingBg)}
          aria-hidden="true"
          data-testid="simrlabs-blurred-backdrop-incoming"
        />
      )}
      <div
        className={styles.pageBackdropOverlay}
        aria-hidden="true"
        data-testid="simrlabs-backdrop-overlay"
      />
      <div className={styles.container}>
        {/* Page Header */}
        <header className={styles.header}>
          <h1 className={styles.pageTitle}>{t.simrlabs.pageTitle}</h1>
          <p className={styles.pageSubtitle}>{t.simrlabs.pageSubtitle}</p>
        </header>

        {/* 1. In-House Games Section */}
        <section className={styles.section} aria-labelledby="simrlabs-games-heading">
          <div className={styles.sectionHeader}>
            <Typography variant="h2" id="simrlabs-games-heading" className={styles.sectionTitle}>
              {t.simrlabs.gamesHeading}
            </Typography>
            <p className={styles.sectionSubtitle}>{t.simrlabs.gamesSubtitle}</p>
          </div>

          {isGamesLoading ? (
            <div className={styles.skeletonGrid} aria-label={t.simrlabs.loadingGames}>
              <div className={styles.skeletonCard} />
            </div>
          ) : gamesError ? (
            <div className={styles.messageBox}>
              <Typography variant="body1">{t.simrlabs.loadError}</Typography>
            </div>
          ) : inHouseGames.length === 0 ? (
            <div className={styles.messageBox}>
              <Typography variant="subtitle1">{t.simrlabs.noGamesFound}</Typography>
            </div>
          ) : (
            <div className={styles.gamesList}>
              {inHouseGames.map((game) => {
                const cardBgUrl =
                  game.id === GameId.SAIL_TO_UNKNOWN
                    ? EXTERNAL_LINKS.SAIL_TO_UNKNOWN_CONTAINER_BG
                    : game.heroBannerUrl;

                return (
                  <article
                    key={game.id}
                    className={styles.gameCard}
                    onMouseEnter={() => handleGameHover(game)}
                  >
                    {cardBgUrl && (
                      <div
                        className={styles.gameCardBackdrop}
                        style={{ backgroundImage: toCssUrl(cardBgUrl) }}
                        aria-hidden="true"
                        data-testid="game-card-backdrop"
                      />
                    )}
                    <div className={styles.gameCardOverlay} aria-hidden="true" />
                    <div className={styles.gameCardContent}>
                      <div className={styles.gameCardHeader}>
                        <span className={styles.gameBadge}>{game.releaseStatus}</span>
                        <div className={styles.gameTags}>
                          {game.genre.slice(0, 3).map((genre) => (
                            <span key={genre} className={styles.gameTag}>
                              {genre}
                            </span>
                          ))}
                        </div>
                      </div>

                      <h3 className={styles.gameTitle}>{game.title}</h3>
                      <p className={styles.gameTagline}>
                        {game.id === GameId.SAIL_TO_UNKNOWN
                          ? t.home.gameSpotlightDesc
                          : game.tagline}
                      </p>

                      <div className={styles.gameActions}>
                        <Link
                          to={getGameProfileRoute(game.slug || game.id)}
                          className={styles.exploreBtn}
                        >
                          {t.simrlabs.exploreGameBtn}
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* 2. In-House VR Mods Section */}
        <section className={styles.section} aria-labelledby="simrlabs-mods-heading">
          <div className={styles.sectionHeader}>
            <Typography variant="h2" id="simrlabs-mods-heading" className={styles.sectionTitle}>
              {t.simrlabs.modsHeading}
            </Typography>
            <p className={styles.sectionSubtitle}>{t.simrlabs.modsSubtitle}</p>
          </div>

          {isModsLoading ? (
            <div className={styles.skeletonGrid} aria-label={t.simrlabs.loadingMods}>
              <div className={styles.skeletonCard} />
              <div className={styles.skeletonCard} />
              <div className={styles.skeletonCard} />
            </div>
          ) : modsError ? (
            <div className={styles.messageBox}>
              <Typography variant="body1">{t.simrlabs.loadError}</Typography>
            </div>
          ) : inHouseMods.length === 0 ? (
            <div className={styles.messageBox}>
              <Typography variant="subtitle1">{t.simrlabs.noModsFound}</Typography>
            </div>
          ) : (
            <div className={styles.modsGrid}>
              {inHouseMods.map((mod) => (
                <ModCard key={mod.id} mod={mod} onMouseEnter={handleModHover} />
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
